import { ReactNode, useCallback, useMemo, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';

/** Space between the target and the ring. */
const PAD = 4;

/** What the card is placed against: the target's box, which outlives the target between two steps. */
export type TourAnchor = { getBoundingClientRect: () => DOMRect };

/** The target's box as a string, so an unchanged box reads as the same snapshot. A detached target has none. */
const boxOf = (target: HTMLElement | null) => {
    if (!target?.isConnected) return '';
    const { top, left, width, height } = target.getBoundingClientRect();
    return [top, left, width, height].join(',');
};

/**
 * Follows the target through scrolling, resizing and its own changes of size. Between two steps there is no
 * target: the last box stays, so the next step's box is reached from it.
 */
function useTargetBox(target: HTMLElement | null) {
    const subscribe = useCallback(
        (onChange: () => void) => {
            if (!target) return () => {};
            const observer = new ResizeObserver(onChange);
            observer.observe(target);
            window.addEventListener('scroll', onChange, true);
            window.addEventListener('resize', onChange);
            return () => {
                observer.disconnect();
                window.removeEventListener('scroll', onChange, true);
                window.removeEventListener('resize', onChange);
            };
        },
        [target]
    );
    const current = useSyncExternalStore(subscribe, () => boxOf(target));
    const [held, setHeld] = useState(current);
    if (current && current !== held) {
        setHeld(current);
    }
    const box = current || held;
    return useMemo(() => {
        if (!box) return undefined;
        const [top, left, width, height] = box.split(',').map(Number);
        return { top, left, width, height };
    }, [box]);
}

/**
 * Dims the page around the step's target and rings it: white, then an amber halo. Until the first target has
 * rendered the whole page is dimmed. From one step to the next the ring moves and resizes from where it is, and
 * `children` gets the box to place the card against, so the card moves with it. It takes no input; the page under
 * it is inert anyway.
 */
export default function TourSpotlight({
    target,
    children,
}: {
    target: HTMLElement | null;
    children: (anchor: TourAnchor) => ReactNode;
}) {
    const box = useTargetBox(target);
    const anchor = useMemo(
        () => box && { getBoundingClientRect: () => new DOMRect(box.left, box.top, box.width, box.height) },
        [box]
    );

    return (
        <>
            {createPortal(
                // Keyed apart: the ring mounts where the first target is, rather than moving out of the full dim.
                box ? (
                    <div
                        key="ring"
                        aria-hidden
                        className="pointer-events-none fixed top-0 left-0 z-(--z-tour-spotlight) rounded-xl shadow-[0_0_0_3px_var(--color-paper),0_0_0_9px_color-mix(in_srgb,var(--secondary)_45%,transparent),0_0_0_9999px_var(--tour-scrim)] motion-safe:transition-[transform,width,height] motion-safe:duration-300 motion-safe:ease-out"
                        // Computed geometry only: the box follows the target.
                        style={{
                            width: box.width + 2 * PAD,
                            height: box.height + 2 * PAD,
                            transform: `translate(${box.left - PAD}px, ${box.top - PAD}px)`,
                        }}
                    />
                ) : (
                    <div
                        key="dim"
                        aria-hidden
                        className="pointer-events-none fixed inset-0 z-(--z-tour-spotlight) bg-(--tour-scrim)"
                    />
                ),
                document.body
            )}
            {anchor && children(anchor)}
        </>
    );
}
